// Rideable-way index for the bike's auto-follow riding mode: every surface
// way the bake ships — roads, paths, railways (ways.json surface runs,
// game/waysdraw.js graphInput) — with all polyline vertices hashed into a
// coarse XZ grid. Pure data + queries; the actual route steering (lock /
// junctions / pure-pursuit) lives in Player._followRoute. Built once by
// WaysNet._load.
//
// Junctions are NOT precomputed: OSM ways are not reliably split at every
// crossing, so a topological graph would miss mid-way branches. Instead the
// follower asks "which other ways pass within a few metres of this vertex"
// at ride time — near() below — which finds shared-node junctions AND
// unsplit crossings alike.
const CELL = 12;          // grid cell (m); must exceed every query radius
const PACK = 65536;       // vertex ref = wi * PACK + pi  (pi < 65536)

export class RoadGraph {
  constructor(geo) {
    // {pts:[[x,y,z]…], kind:'road'|'path'|'rail', name:string, run:int,
    // tarmac:bool} — run = the ways.json run index (graphInput; -1 when the
    // input has none); tarmac = a paved road, not a path (`paved` set per
    // road by ways2.js from its run: furniture benches)
    this.ways = [];
    const add = (pts, kind, name, run, tarmac = false) => {
      if (pts && pts.length >= 2 && pts.length < PACK
          && this.ways.length < 32000) this.ways.push({ pts, kind, name: name || '', run: Number.isInteger(run) ? run : -1, tarmac });
    };
    for (const r of geo.roads || []) add(r.pts, r.cls === 'path' ? 'path' : 'road', r.name, r.run, r.cls !== 'path' && r.paved === true);
    for (const r of geo.rails || []) add(r.pts, 'rail', '', r.run);
    this.grid = new Map();  // cell key -> packed vertex refs
    for (let wi = 0; wi < this.ways.length; wi++) {
      const pts = this.ways[wi].pts;
      for (let pi = 0; pi < pts.length; pi++) {
        const k = key(pts[pi][0], pts[pi][2]);
        let a = this.grid.get(k);
        if (!a) this.grid.set(k, a = []);
        a.push(wi * PACK + pi);
      }
    }
  }

  // every way-vertex within r (horizontal) and dy (vertical — keeps a bridge
  // deck from matching the underpass below it) of (x,y,z): fn(wi, pi, d2)
  near(x, y, z, r, dy, fn) {
    const r2 = r * r;
    const cx0 = Math.floor((x - r) / CELL), cx1 = Math.floor((x + r) / CELL);
    const cz0 = Math.floor((z - r) / CELL), cz1 = Math.floor((z + r) / CELL);
    for (let cx = cx0; cx <= cx1; cx++) {
      for (let cz = cz0; cz <= cz1; cz++) {
        const a = this.grid.get((cx + 2048) * 8192 + (cz + 2048));
        if (!a) continue;
        for (const ref of a) {
          const wi = (ref / PACK) | 0, pi = ref % PACK;
          const p = this.ways[wi].pts[pi];
          const dx = p[0] - x, dz = p[2] - z;
          const d2 = dx * dx + dz * dz;
          if (d2 <= r2 && Math.abs(p[1] - y) <= dy) fn(wi, pi, d2);
        }
      }
    }
  }

  // nearest vertex to (x,y,z) within r / dy → {wi, pi} or null
  nearest(x, y, z, r, dy) {
    let best = null, bd = Infinity;
    this.near(x, y, z, r, dy, (wi, pi, d2) => {
      if (d2 < bd) { bd = d2; best = { wi, pi }; }
    });
    return best;
  }

  // road name at the nearest vertex within r / dy, or '' if none
  nameAt(x, y, z, r, dy) {
    const hit = this.nearest(x, y, z, r, dy);
    return hit ? this.ways[hit.wi].name : '';
  }

  // unit XZ tangent of way wi at vertex pi looking in direction dir (skips
  // ~2 vertices for a stable heading over the 4 m densification); null at a
  // degenerate end
  tangent(wi, pi, dir) {
    const pts = this.ways[wi].pts;
    let j = Math.max(0, Math.min(pts.length - 1, pi + dir * 2));
    if (j === pi) j = Math.max(0, Math.min(pts.length - 1, pi + dir));
    if (j === pi) return null;
    // j already lies in the travel direction, so pts[pi] → pts[j] IS the tangent
    const dx = pts[j][0] - pts[pi][0], dz = pts[j][2] - pts[pi][2];
    const l = Math.hypot(dx, dz);
    if (l < 1e-6) return null;
    return { x: dx / l, z: dz / l };
  }
}

function key(x, z) {
  return (Math.floor(x / CELL) + 2048) * 8192 + (Math.floor(z / CELL) + 2048);
}
